import { ChangeDetectorRef, Component, Injector, OnInit, ViewContainerRef } from "@angular/core";
import { NoteControlService } from "ng/src/app/notes/note-controller/note-control.service";
import { Dialog } from "primeng/dialog";
import { DialogDetails, ViewContainerDetails } from "ng/src/app/notes/note-controller/note-controller.model";
import { ResizeObserverHelper } from "ng/src/app/core/resize-observer-helper";
import { Note } from "ng/src/app/notes/shared/note.model";
import { TemplateTypeId } from "ng/src/app/templates/shared/template.model";
import { TocMode } from "ng/src/app/notes/shared/note-entry.model";
import { AccordionTab } from "primeng/accordion";
import { merge } from "rxjs";
import { delay, filter, map, takeWhile, tap } from "rxjs/operators";
import { Menu } from "primeng/menu";
import { ConfirmationService, MenuItem } from "primeng/api";


@Component({
	selector: 'lb-note-controller',
	templateUrl: 'note-controller.component.html',
	styleUrls: ['note-controller.component.scss']
})
export class NoteControllerComponent implements OnInit {
	
	inProgressVisible = false;
	private dialogs = new Map<Dialog, DialogDetails>();
	
	get entries() {
		return this.controlService.popup
	}
	get minimizedEntries() {
		return this.controlService.bar;
	}
	get hiddenEntries() {
		return this.controlService.hidden;
	}
	
	get noteBarWidth(): string {
		const width = this.controlService.barWidth;
		return width >= 0 ? `${width}px` : '500px';
	}
	
	constructor(
		public readonly controlService: NoteControlService,
		private readonly confirmService: ConfirmationService,
		private readonly changeRef: ChangeDetectorRef
	) {
	}
	
	ngOnInit(): void {
		ResizeObserverHelper.observe(document.documentElement).subscribe((size) => {
			this.controlService.currentWidth = size.contentRect.width;
			
			const { right, bottom } = size.contentRect;
				
			Array.from(this.dialogs.entries()).forEach(([dialog, details], index) => {
				if (!details.element) return;
				
				const rect = details.element.getBoundingClientRect();
				let needsUpdate = false;
				
				if (this.controlService.shouldUseMobileView()) {
					if (index === 0) {
						needsUpdate = this.setMaximized(dialog, true);
					}
					else {
						this.controlService.sendToBar(details.details, true);
					}
				}
				else if (!dialog.maximized && (rect.right > right || rect.bottom > bottom))
					this.controlService.sendToBar(details.details);
				
				if (needsUpdate)
					details.changeDetector.markForCheck();
				
			});
			
		});
		
		this.controlService.update$.subscribe(() => this.changeRef.detectChanges());
	}
	
	
	viewContainerReady(container: ViewContainerRef, details: ViewContainerDetails) {
		details.container = container;
	}
	
	registerDialog(dialog: Dialog, details: ViewContainerDetails, dialogInjector: Injector) {
		let needsMaximized = details.dialogSettings.maximized;
		const resizeSub = merge(
			dialog.onResizeEnd,
			dialog.onDragEnd
		)
			.pipe(
				filter(() => !!dialog.container && !dialog.maximized),
				map(() => dialog.container)
			)
			.subscribe((el) => {
				const rect = el.getBoundingClientRect();
				const stylePrefs = details.dialogSettings.style;
				const coordinates = details.dialogSettings.coordinates;
				
				stylePrefs.height = rect.height + 'px';
				stylePrefs.width = rect.width + 'px';
				
				coordinates.left = rect.x + 'px';
				coordinates.top = rect.y + 'px';
			});
		
		const dialogDetails: DialogDetails = {
			details: details,
			changeDetector: dialogInjector.get(ChangeDetectorRef),
			subscription: merge(
					dialog.onShow,
					dialog.onHide,
					dialog.onMaximize
				)
				.pipe(
					takeWhile(() => this.dialogs.has(dialog)),
					map(() => !!dialog.visible),
					tap((visible) => {
						details.dialogSettings.maximized = dialog.maximized;
						
						if (needsMaximized && visible) {
							needsMaximized = false;
							this.setMaximized(dialog, true);
							dialogDetails.changeDetector.markForCheck();
						}
						
						if (!dialog.maximized) {
							this.setDialogPosition(dialog, details.dialogSettings);
						}
					}),
					delay(0)
				)
				.subscribe(visible => {
					if (!visible) {
						delete dialogDetails.element;
						return;
					}
					
					const container = dialogDetails.element = dialog.container;
					container.addEventListener('mousedown', () => dialog.moveOnTop());
					const el = container.querySelector<HTMLDivElement>('.p-resizable-handle');
					if (el) {
						el.innerHTML = '<i class="fa fa-caret-right" style="transform: rotate(45deg)"></i>'
					}
					
				})
				.add(() => resizeSub.unsubscribe())
		}
		
		this.dialogs.set(dialog, dialogDetails);
	}
	
	unregisterDialog(dialog: Dialog) {
		this.dialogs.get(dialog)?.subscription?.unsubscribe();
		this.dialogs.delete(dialog);
	}
	
	registerTab(tab: AccordionTab) {
		const originalToggle = tab.toggle.bind(tab);
		tab.toggle = (event: Event) => {
			const path = event.composedPath();
			
			// Prevent clicks from the header buttons from triggering the toggle event as a side effect
			if ((path as HTMLElement[]).some(target => target.nodeName === 'BUTTON')) return false;
			
			return originalToggle(event);
		};
	}
	
	getWindowName(container: ViewContainerDetails) {
		const comp = container.component.instance;
		const service = comp.noteEntryService;
		const note = service.editingNote;
		
		if (!service.isReady || service.isLoadingTemplate)
			return 'Loading Template';
		else if (note)
			return Note.getNoteName(note);
		else if (service.selectedTemplate) {
			const templateName = service.selectedTemplate.name;
			if (service.selectedTemplate.templateType.id === TemplateTypeId.INSPECTION ||
				!(comp.selectedNode?.label && comp.mode === TocMode.EDITOR)
			)
				return templateName;
			
			return `${templateName}\r\n${comp.selectedNode.label}`;
		}
		return 'Note Entry';
	}
	
	adjustDialogHeaderSizing(entry: ResizeObserverEntry) {
		const el = entry.target as HTMLElement;
		const analyzeText = el.innerText;
		const maxWordSize = Math.floor(el.getBoundingClientRect().width / 16);
		const hasWordsOverSize = analyzeText.split(' ').some(word => word.length > maxWordSize);
		
		el.style.wordBreak = (hasWordsOverSize) ? 'break-all' : '';
	}
	
	showOverFlowMenu(menu: Menu, dialog: Dialog, event: Event) {
		const dialogDetails = this.dialogs.get(dialog)!;
		const savePosition: MenuItem = {
			label: 'Save Position',
			icon: 'fa fa-save',
			title: 'Save this popup\'s position to be where new popups will appear',
			command: () => this.confirmService.confirm({
				key: 'dialog-save-settings',
				message: `This will save how this popup is currently displayed and any new popups will open to this setting.\n
					If the dialog is unable to match your screen settings it will appear with the default options.\n
					You can delete these settings in your user profile settings.`,
				rejectButtonStyleClass: 'p-button-secondary',
				defaultFocus: 'reject',
				accept: () => ViewContainerDetails.userDefaultDialogSettings(dialogDetails.details.dialogSettings)
			})
		};
		
		menu.model = [
			{
				label: dialog.maximized ? 'Floating Window' : 'Fullscreen',
				title: dialog.maximized ? 'Set the popup to be resizable and movable around the screen' : 'Set the popup to take as much room as needed',
				icon: 'fa fa-window-restore',
				command: () => {
					this.setMaximized(dialog, !dialog.maximized);
					this.setDialogPosition(dialog, dialogDetails.details.dialogSettings);
					dialogDetails.changeDetector.markForCheck();
				}
			}
		];
		
		if (!dialog.maximized)
			menu.model.push(savePosition);
		
		menu.show(event);
	}
	
	setDialogPosition(dialog: Dialog, settings: ViewContainerDetails['dialogSettings']) {
		const windowRect = document.documentElement.getBoundingClientRect();
		const container = dialog.container;
		
		if (!container || dialog.maximized) return false;
		
		container.style.position = 'absolute';
		if (settings.coordinates.top)
			container.style.top = settings.coordinates.top;
		
		if (settings.coordinates.left)
			container.style.left = settings.coordinates.left;
		
		Object.entries(settings.style).forEach(entry => {
			container.style[entry[0] as keyof ViewContainerDetails['dialogSettings']['style']] = entry[1];
		});
		
		const currentRect = container.getBoundingClientRect();
		
		if (currentRect.right > windowRect.right || currentRect.bottom > windowRect.bottom) {
			container.style.position = 'relative';
			container.style.top = container.style.left = '';
			container.style.width = container.style.height = '500px';
		}
		
		const contentEl = dialog.contentViewChild.nativeElement as HTMLElement;
		contentEl.style.height = '100%';
		
		
		return true;
	}
	
	private setMaximized(dialog: Dialog, maximized: boolean) {
		if (!!dialog.maximized === maximized) return false;
		dialog.modal = maximized;
		dialog.resizable = !maximized;
		dialog.maximize();
		
		const contentChildStyle = (dialog.contentViewChild.nativeElement as HTMLElement).style;
		const container = dialog.container;
		
		if (container && contentChildStyle) {
			if (!dialog.maximized) {
				contentChildStyle.height = container.style.width = container.style.height = '500px';
			}
			else {
				contentChildStyle.width = contentChildStyle.height = container.style.minWidth = container.style.minHeight = container.style.width = container.style.height = '';
			}
		}
		
		return true;
	}
}